import React, { useState } from "react";
import { useBluelagoon } from "@bluelagoon";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@shadcn-in-nx/ui/button";
import { Loader2 } from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@shadcn-in-nx/ui/select";
import { Label } from "@shadcn-in-nx/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@shadcn-in-nx/ui/dialog";

export default function SubscribeConfirmModal({ open, onOpenChange, blueprint, shareToken }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [updatePolicy, setUpdatePolicy] = useState("AUTO");
    const { call: subscribe, isLoading } = useBluelagoon({ method: "subscribeToBlueprint" });

    const handleSubscribe = async () => {
        const res = await subscribe({
            id: blueprint.id,
            shareToken,
            updatePolicy,
        });
        const result = res?.data;
        if (result?.workflowId) {
            toast.success(t("subscriptions.subscribed"));
            onOpenChange(false);
            navigate(`/chat/${result.workflowId}/chat`);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{t("subscriptions.subscribeTitle")}</DialogTitle>
                    <DialogDescription>
                        {t("subscriptions.subscribeDescription", { name: blueprint?.name })}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label>{t("subscriptions.updatePolicy")}</Label>
                        <Select value={updatePolicy} onValueChange={setUpdatePolicy}>
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="AUTO">{t("subscriptions.policyAuto")}</SelectItem>
                                <SelectItem value="MANUAL">{t("subscriptions.policyManual")}</SelectItem>
                            </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                            {updatePolicy === "AUTO"
                                ? t("subscriptions.policyAutoDescription")
                                : t("subscriptions.policyManualDescription")}
                        </p>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        {t("common.cancel")}
                    </Button>
                    <Button onClick={handleSubscribe} disabled={isLoading}>
                        {isLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                        {t("subscriptions.subscribe")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
